import { ExclamationCircle } from '@ixigo/icons';
import Button from '@ixigo/iui/Button';
import Typography from '@ixigo/iui/Typography';
import clsx from 'clsx';
import { ChangeEvent, useEffect, useState } from 'react';
import { uploadFile } from '../../apis/common';
import { TestId } from '../../enums/test';
import placeholder from '../../images/filePlaceholder.svg';
import BottomsheetHeader from '../BottomsheetHeader';
import Dialog from '../Dialog';
import SpinnerLoader from '../Loader/Spinner';
import Toast from '../Toast';
import { UploaderProps } from './index.types';

function Uploader({ label, onUpload, className }: UploaderProps) {
    const [file, setFile] = useState<File>();
    const [toast, setToast] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!file) setLoading(false);
    }, [file]);

    const validateInputFile = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files![0];

        if (selectedFile.size / (1024 * 1024) > 3) {
            setToast(true);
            e.target.value = '';
            return null;
        }

        return selectedFile;
    };

    const selectFile = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = validateInputFile(e);
        if (!selectedFile) return;

        setFile(selectedFile);
    };

    const renderImage = () => {
        if (!file) return null;
        const isImg = file.type.includes('image');
        return (
            <img
                src={isImg ? URL.createObjectURL(file) : placeholder}
                className={clsx('max-h-44', isImg && 'border border-neutral-100')}
                alt={file.name}
            />
        );
    };

    const upload = () => {
        setLoading(true);

        const formData = new FormData();

        formData.append('file', file!);
        uploadFile(formData).then((res) => {
            onUpload(file!, res);
            setFile(undefined);
            setLoading(false);
        });
    };

    return (
        <>
            <label htmlFor='uploadFile' className={className} data-testid={TestId.UPLOADER}>
                <input id='uploadFile' type='file' placeholder='Upload File' className='hidden' onChange={selectFile} />
                {label}
            </label>
            <Dialog open={!!file} classes={{ container: '!min-w-full' }}>
                <BottomsheetHeader title='Upload Proof' onClose={() => setFile(undefined)} />
                <div className='flex flex-col grow gap-15 justify-center items-center p-30'>
                    {renderImage()}
                    <Typography variant='body-md' fontWeight='medium' className='text-charcoal-600'>
                        {file?.name}
                    </Typography>
                </div>
                <div className='flex justify-center items-center px-20 py-10 border-t border-charcoal-100'>
                    <Button size='lg' onClick={upload} disabled={loading}>
                        {loading ? <SpinnerLoader className='px-20' size='sm' /> : 'Upload'}
                    </Button>
                </div>
            </Dialog>
            <Toast
                heading='Cannot upload files larger than 3MB'
                onBackdropClick={() => setToast(false)}
                iconLeft={<ExclamationCircle fontSize={20} />}
                visible={toast}
            />
        </>
    );
}

export default Uploader;
